import NitroService from "../../../utils/nitro.service";
import {
  FavoritePriceShowAs,
  IFavoriteOriginAdditionalType,
  IFavoriteOriginMonth,
  IFavoriteOriginWithMonths,
  IFavoriteOriginPeriod,
  ICreatePeriodPriceResponse,
  MonthColumn,
  SendPanelCustomRow,
} from "../../../types/IFavoriteOrigins";
import { MONTHS_LOCAL } from "../../../types/IMonth";
import { mapFavoritesByMarketerToLegacy } from "../../../utils/favoriteOriginsMapper";

export const toDateTimeParts = (date: Date = new Date()) => ({
  year: date.getFullYear(),
  month: date.getMonth() + 1,
  day: date.getDate(),
  hour: date.getHours(),
  minute: date.getMinutes(),
  second: date.getSeconds(),
  nanosecond: date.getMilliseconds() * 1_000_000,
});

export const getFavoriteSetId = (product: string, marketer: string) =>
  `${marketer}|${product}`;

export const getFavoriteLocId = (
  product: string,
  origin: IFavoriteOriginWithMonths,
) =>
  origin.id ||
  `${product}|${origin.location.id}|${origin.competitor ? "COMPETITOR" : "KOCH"}`;

export const buildOrderedFavLocMaps = (
  product: string,
  favorites: IFavoriteOriginWithMonths[],
  createdIdsByRowKey: Map<string, string>,
  getRowKey: (origin: IFavoriteOriginWithMonths) => string,
) => {
  const orderedFavorites = [
    ...favorites.filter((favorite) => !favorite.competitor),
    ...favorites.filter((favorite) => favorite.competitor),
  ]
    .map((favorite, index) => ({ favorite, index }))
    .sort((left, right) => {
      const leftOrder = Number.isFinite(left.favorite.order)
        ? Number(left.favorite.order)
        : Infinity;
      const rightOrder = Number.isFinite(right.favorite.order)
        ? Number(right.favorite.order)
        : Infinity;

      if (left.favorite.competitor !== right.favorite.competitor) {
        return Number(left.favorite.competitor) - Number(right.favorite.competitor);
      }
      if (leftOrder !== rightOrder) {
        return leftOrder - rightOrder;
      }
      return left.index - right.index;
    })
    .map(({ favorite }) => favorite);

  return orderedFavorites
    .map((favorite, order) => {
      const rowKey = getRowKey(favorite);
      const id = createdIdsByRowKey.get(rowKey) || getFavoriteLocId(product, favorite);
      if (!id) return null;
      return { id, order };
    })
    .filter(Boolean) as Array<{ id: string; order: number }>;
};

export const refreshFavoritesByMarketer = async (
  marketer: string,
  setFavoriteOriginsByUser: (favorites: any[]) => void,
) => {
  const normalized = await NitroService.getFavoritesByMarketer({ marketer });
  const mapped = mapFavoritesByMarketerToLegacy(normalized);
  setFavoriteOriginsByUser(mapped);
  return mapped;
};

export const reorderFavorites = async (
  marketer: string,
  product: string,
  favorites: IFavoriteOriginWithMonths[],
  getRowKey: (origin: IFavoriteOriginWithMonths) => string,
  createdIdsByRowKey: Map<string, string> = new Map<string, string>(),
) => {
  const orderedFavLocMaps = buildOrderedFavLocMaps(
    product,
    favorites,
    createdIdsByRowKey,
    getRowKey,
  );

  if (orderedFavLocMaps.length === 0) {
    return;
  }

  await NitroService.reorderFavorites({
    favSetId: getFavoriteSetId(product, marketer),
    orderedFavLocMaps,
  });
};

export const createFavorites = async (
  marketer: string,
  product: string,
  rowsToAdd: IFavoriteOriginWithMonths[],
  favoritesWithOrder: IFavoriteOriginWithMonths[],
  getRowKey: (origin: IFavoriteOriginWithMonths) => string,
) => {
  const createdIdsByRowKey = new Map<string, string>();

  await Promise.all(
    rowsToAdd.map(async (row) => {
      const created = await NitroService.createFavorite({
        marketer,
        product,
        locationId: row.location.id,
        competitor: row.competitor,
        order: Math.max(
          0,
          favoritesWithOrder.findIndex(
            (favorite) => getRowKey(favorite) === getRowKey(row),
          ),
        ),
      });
      const createdId = created[0]?.favLocId;
      if (createdId) {
        createdIdsByRowKey.set(getRowKey(row), createdId);
      }
    }),
  );

  return createdIdsByRowKey;
};

export const deleteFavorite = async (
  marketer: string,
  product: string,
  origin: IFavoriteOriginWithMonths,
) => {
  await NitroService.deleteFavorite({
    favSetId: getFavoriteSetId(product, marketer),
    favLocId: getFavoriteLocId(product, origin),
  });
};

export const createFavorite = async (
  marketer: string,
  product: string,
  origin: IFavoriteOriginWithMonths,
  order: number,
) => {
  return NitroService.createFavorite({
    marketer,
    product,
    locationId: origin.location.id,
    competitor: origin.competitor,
    order,
  });
};

export const persistPeriodInfo = async (
  marketer: string,
  updatedOrigin: IFavoriteOriginWithMonths,
) => {
  const updatedAt = toDateTimeParts();
  let skippedForMissingPeriodId = 0;
  const periodInfoList = updatedOrigin.months.flatMap((monthEntry) =>
    monthEntry.periods.flatMap((period) =>
      (period.prices.additionalTypes || [])
        .filter((row) => row.name.trim() !== "")
        .map((row, index) => {
          let value = "";
          if (row.value !== undefined) {
            value = row.value;
          } else if (row.price !== null) {
            value = String(row.price);
          }

          return {
            periodId: period.id || "",
            updatedBy: marketer,
            updatedAt,
            name: row.name,
            value,
            order: row.order ?? index + 1,
            valueType: "STRING" as const,
          };
        })
        .filter((row) => {
          if (row.periodId !== "") return true;
          skippedForMissingPeriodId += 1;
          return false;
        }),
    ),
  );

  if (skippedForMissingPeriodId > 0) {
    console.warn(
      `persistPeriodInfo: skipped ${skippedForMissingPeriodId} row(s) because their period has not been created on the server yet.`,
    );
  }

  await NitroService.createPeriodInfo({ periodInfoList });
};

const createPlaceholderPeriod = async (
  marketer: string,
  product: string,
  origin: IFavoriteOriginWithMonths,
  monthNum: number,
  year: number,
  half: MonthColumn["half"],
) => {
  const lastDay = new Date(year, monthNum, 0).getDate();
  const startDate = { year, month: monthNum, day: half === "h2" ? 16 : 1 };
  const endDate = {
    year,
    month: monthNum,
    day: half === "h1" ? 15 : lastDay,
  };

  const created = (
    await NitroService.createPeriodPrice({
      favLocId: origin.id || getFavoriteLocId(product, origin),
      month: { year, month: monthNum, day: 1 },
      split: half !== "full",
      startDate,
      endDate,
      name: "posted",
      price: null,
      showAs: "DISCUSS",
      updatedBy: marketer,
    })
  )[0];

  if (!created?.periodId) return null;

  return {
    favMonthId: created.favMonthId,
    periodId: created.periodId,
    startDate,
    endDate,
  };
};

export const persistAdditionalTypes = async (
  marketer: string,
  product: string,
  sendPanelOrigin: IFavoriteOriginWithMonths,
  customRows: SendPanelCustomRow[],
  monthColumns: MonthColumn[],
) => {
  const activeCustomRows = customRows.filter(
    (row) => row.priceType.trim() !== "",
  );

  const columnsNeedingPeriod = monthColumns.filter((col, colIndex) => {
    const colMonthNum = MONTHS_LOCAL.indexOf(col.month) + 1;
    const hasExistingMonth = sendPanelOrigin.months.some(
      (m) => m.month.month === colMonthNum,
    );
    if (hasExistingMonth) return false;
    const field = `month-${colIndex}`;
    return activeCustomRows.some(
      (row) => (row.values[field] ?? "").trim() !== "",
    );
  });

  const currentMonthIndex = new Date().getMonth();
  const currentYear = new Date().getFullYear();

  const newlyCreatedMonths = (
    await Promise.all(
      columnsNeedingPeriod.map(
        async (col): Promise<IFavoriteOriginMonth | null> => {
          const monthNum = MONTHS_LOCAL.indexOf(col.month) + 1;
          const year =
            MONTHS_LOCAL.indexOf(col.month) >= currentMonthIndex
              ? currentYear
              : currentYear + 1;

          const created = await createPlaceholderPeriod(
            marketer,
            product,
            sendPanelOrigin,
            monthNum,
            year,
            col.half,
          );
          if (!created) return null;

          return {
            id: created.favMonthId,
            month: { year, month: monthNum, day: 1 },
            isSplit: col.half !== "full",
            periods: [
              {
                id: created.periodId,
                startDate: created.startDate,
                endDate: created.endDate,
                prices: { posted: null, truck: null, rail: null },
              },
            ],
          };
        },
      ),
    )
  ).filter((month): month is IFavoriteOriginMonth => month !== null);

  const updatedOrigin: IFavoriteOriginWithMonths = {
    ...sendPanelOrigin,
    months: [...sendPanelOrigin.months, ...newlyCreatedMonths].map(
      (monthEntry) => ({
        ...monthEntry,
        periods: monthEntry.periods.map((period) => {
          const monthNum = monthEntry.month.month;
          const monthIsSplitInUI = monthColumns.some(
            (c) =>
              MONTHS_LOCAL.indexOf(c.month) + 1 === monthNum &&
              c.half !== "full",
          );
         
          const periodHalf: "h1" | "h2" =
            period.startDate.day <= 15 ? "h1" : "h2";
          const matchingColumnIndex = monthColumns.findIndex((col) => {
            const colMonthNum = MONTHS_LOCAL.indexOf(col.month) + 1;
            if (colMonthNum !== monthNum) return false;
            if (monthIsSplitInUI) {
              return col.half === periodHalf;
            }
            return col.half === "full";
          });

          const additionalTypes: IFavoriteOriginAdditionalType[] =
            activeCustomRows.map((row, rowIndex) => {
              const existingType = (
                period.prices.additionalTypes ?? []
              ).find(
                (additionalType) =>
                  additionalType.name.trim() === row.priceType.trim(),
              );
              const rawValue =
                matchingColumnIndex === -1
                  ? existingType?.value ??
                    (existingType?.price != null
                      ? String(existingType.price)
                      : "")
                  : row.values[`month-${matchingColumnIndex}`] ?? "";
              const parsed =
                rawValue !== undefined && rawValue !== ""
                  ? parseFloat(rawValue)
                  : NaN;
              return {
                name: row.priceType,
                price: Number.isFinite(parsed) ? parsed : null,
                showAs: null as null,
                value: rawValue ?? "",
                order: rowIndex + 1,
              };
            });

          return {
            ...period,
            prices: {
              ...period.prices,
              additionalTypes,
            },
          };
        }),
      }),
    ),
  };

  await persistPeriodInfo(marketer, updatedOrigin);
};

export const toggleSplitMonth = async (
  marketer: string,
  monthEntry: IFavoriteOriginMonth,
  shouldUnsplit: boolean,
) => {
  if (!monthEntry.id) {
    return;
  }

  const lastDay = new Date(monthEntry.month.year, monthEntry.month.month, 0).getDate();
  const updatedAt = toDateTimeParts();

  if (shouldUnsplit) {
    return NitroService.unsplitMonth({
      favMonthId: monthEntry.id,
      newFirstPeriodEndDate: {
        year: monthEntry.month.year,
        month: monthEntry.month.month,
        day: lastDay,
      },
      updatedBy: marketer,
      updatedAt,
    });
  }

  return NitroService.splitMonth({
    favMonthId: monthEntry.id,
    newFirstPeriodEndDate: {
      year: monthEntry.month.year,
      month: monthEntry.month.month,
      day: 15,
    },
    updatedBy: marketer,
    updatedAt,
  });
};

export const persistSplitStateDelta = async (
  marketer: string,
  sourceOrigin: IFavoriteOriginWithMonths,
  editedOrigin: IFavoriteOriginWithMonths,
) => {
  const updatedAt = toDateTimeParts();
  await Promise.all(
    editedOrigin.months.map(async (monthEntry) => {
      const sourceMonth = sourceOrigin.months.find(
        (month) =>
          month.month.year === monthEntry.month.year &&
          month.month.month === monthEntry.month.month,
      );

      if (!monthEntry.id || !sourceMonth || sourceMonth.isSplit === monthEntry.isSplit) {
        return;
      }

      const lastDay = new Date(monthEntry.month.year, monthEntry.month.month, 0).getDate();

      if (monthEntry.isSplit) {
        await NitroService.splitMonth({
          favMonthId: monthEntry.id,
          newFirstPeriodEndDate: {
            year: monthEntry.month.year,
            month: monthEntry.month.month,
            day: 15,
          },
          updatedBy: marketer,
          updatedAt,
        });
        return;
      }

      await NitroService.unsplitMonth({
        favMonthId: monthEntry.id,
        newFirstPeriodEndDate: {
          year: monthEntry.month.year,
          month: monthEntry.month.month,
          day: lastDay,
        },
        updatedBy: marketer,
        updatedAt,
      });
    }),
  );
};

const inFlightPeriodPriceRequests = new Map<
  string,
  Promise<ICreatePeriodPriceResponse | undefined>
>();

export const persistPeriodPrice = async (params: {
  marketer: string;
  product: string;
  editedOrigin: IFavoriteOriginWithMonths;
  monthEntry: IFavoriteOriginMonth;
  period: IFavoriteOriginPeriod;
  mode: "posted" | "truck" | "rail";
}) => {
  const { marketer, product, editedOrigin, monthEntry, period, mode } = params;
  const favLocId = editedOrigin.id || getFavoriteLocId(product, editedOrigin);
  const requestKey = [
    favLocId,
    monthEntry.month.year,
    monthEntry.month.month,
    period.startDate.day,
    period.endDate.day,
    mode,
  ].join("|");

  const existingRequest = inFlightPeriodPriceRequests.get(requestKey);
  if (existingRequest) {
    return existingRequest;
  }

  const run = async () => {
    const periodPrice = period.prices[mode];
    const showAs: FavoritePriceShowAs = periodPrice?.showAs || "PRICE";
    const price = periodPrice?.price ?? null;

    if (periodPrice?.id) {
      await NitroService.updatePeriodPrice({
        periodPriceId: periodPrice.id,
        price,
        showAs,
        updatedBy: marketer,
      });
      return undefined;
    }

    if (price === null && showAs === "PRICE") {
      return undefined;
    }

    const created = await NitroService.createPeriodPrice({
      favLocId,
      month: {
        year: monthEntry.month.year,
        month: monthEntry.month.month,
        day: 1,
      },
      split: monthEntry.isSplit,
      startDate: period.startDate,
      endDate: period.endDate,
      name: mode,
      price,
      showAs,
      updatedBy: marketer,
    });

    return created[0];
  };

  const request = run().finally(() => {
    inFlightPeriodPriceRequests.delete(requestKey);
  });
  inFlightPeriodPriceRequests.set(requestKey, request);
  return request;
};
